import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ProjectExecutionActivationApi from "../../../../../infrastructure/api/projectExecutionActivation";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("fa-IR");
};

const activationColor = (statusTitle) => {
  const text = String(statusTitle || "");
  if (text.includes("فعال") && !text.includes("غیر")) return "success";
  if (text.includes("معلق")) return "warning";
  if (text.includes("لغو")) return "error";
  if (text.includes("انتظار")) return "info";
  return "default";
};

const ProjectExecutionActivationPage = ({ source = "Management" }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [notes, setNotes] = useState("");

  const areaLabel = source === "Management" ? "مدیریت" : "اپراتور سولارنت";
  const basePath =
    source === "Management" ? "/SLN/Management" : "/SLN/SolarentOperator";

  const loadItems = async () => {
    setLoading(true);
    setError("");
    const res = await ProjectExecutionActivationApi.getReadyForExecutionActivations(source);
    if (!res.ok) {
      setError(res.message || "خطا در دریافت لیست پروژه‌های آماده فعال‌سازی");
      setItems([]);
    } else {
      setItems(Array.isArray(res.data) ? res.data : []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadItems();
  }, [source]);

  const handleActivate = async (item) => {
    if (!item?.projectContractId) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionActivationApi.activateProjectExecution(source, {
      projectContractId: item.projectContractId,
      notes: notes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "فعال‌سازی ناموفق بود.");
    } else {
      setSuccess(
        `${res.message || "فعال‌سازی ثبت شد."} — ${res.data?.activationStatusTitle || "Active"}`
      );
      setNotes("");
      await loadItems();
    }
    setSubmitting(false);
  };

  const handleSuspend = async (item) => {
    if (!item?.id) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionActivationApi.suspendActivation(source, {
      projectExecutionActivationId: item.id,
      notes: notes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "تعلیق ناموفق بود.");
    } else {
      setSuccess(res.message || "فعال‌سازی معلق شد.");
      setNotes("");
      await loadItems();
    }
    setSubmitting(false);
  };

  const handleCancel = async (item) => {
    if (!item?.id) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const res = await ProjectExecutionActivationApi.cancelActivation(source, {
      projectExecutionActivationId: item.id,
      notes: notes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "لغو ناموفق بود.");
    } else {
      setSuccess(res.message || "فعال‌سازی لغو شد.");
      setNotes("");
      await loadItems();
    }
    setSubmitting(false);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent
          items={[
            { title: "SLN", path: "/SLN" },
            { title: areaLabel, path: basePath },
            { title: "پروژه‌های آماده فعال‌سازی اجرا" },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="پروژه‌های آماده فعال‌سازی اجرا"
            subheader="قراردادهای آماده اجرا — پس از تایید نهایی و پرداخت فاکتور الزامی، فعال‌سازی اجرا از اینجا انجام می‌شود."
            action={
              <IconButton onClick={loadItems} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            }
          />
          <CardContent>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}
            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            ) : null}

            <TextField
              label="یادداشت (اختیاری)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />

            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>درخواست</TableCell>
                    <TableCell>پیمانکار</TableCell>
                    <TableCell>مبلغ</TableCell>
                    <TableCell>حالت اجرا / مالی</TableCell>
                    <TableCell>وضعیت فعال‌سازی</TableCell>
                    <TableCell align="center">عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {loading
                          ? "در حال بارگذاری..."
                          : "پروژه‌ای آماده فعال‌سازی اجرا نیست."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.projectContractId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.requestTitle || `درخواست ${item.requestId}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            قرارداد #{item.projectContractId} — کد {item.requestReferenceId}
                          </Typography>
                          {item.activatedAt ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {toJalaliFullDate(item.activatedAt)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{item.contractorTitle || "-"}</TableCell>
                        <TableCell>{formatPrice(item.contractPrice)} ریال</TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {item.executionModeTitle || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.financingModeTitle || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                              size="small"
                              color={activationColor(item.activationStatusTitle)}
                              label={item.activationStatusTitle || "در انتظار"}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={item.projectContractStatusTitle || "-"}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                            {item.canActivate ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<PlayCircleOutlineIcon />}
                                disabled={loading || submitting}
                                onClick={() => handleActivate(item)}
                              >
                                فعال‌سازی
                              </Button>
                            ) : null}
                            {item.canSuspend ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<PauseCircleOutlineIcon />}
                                disabled={loading || submitting}
                                onClick={() => handleSuspend(item)}
                              >
                                تعلیق
                              </Button>
                            ) : null}
                            {item.canCancel ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelOutlinedIcon />}
                                disabled={loading || submitting}
                                onClick={() => handleCancel(item)}
                              >
                                لغو
                              </Button>
                            ) : null}
                            {!item.canActivate &&
                            !item.canSuspend &&
                            !item.canCancel &&
                            item.activationBlockedReason ? (
                              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }}>
                                {item.activationBlockedReason}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ProjectExecutionActivationPage;
